<script setup lang="ts">
  import { formatNameNumber } from '@bv/utils/common/string/format/name-number';
  import { trans } from '@bv/utils/i18n';
  import { storeToRefs } from 'pinia';
  import { ref, computed } from 'vue';

  import { livePanelStore } from '@/stores/socket/live-panel-socket.store';

  import type { Header } from '@bv/components/table/BvTable.vue';

  type TabItemValue = 'agents' | 'queues';

  interface TabItem {
    title: string;
    value: TabItemValue;
  }
  const store = livePanelStore();

  const { availableAgents, queues, uiFlags } = storeToRefs(store);

  const tablesItems = computed<TabItem[]>(() => [
    { title: trans('call_center.agents_summary'), value: 'agents' },
    { title: trans('call_center.queues_summary'), value: 'queues' },
  ]);

  const selectedTab = ref<TabItemValue>('agents');

  const selectedTabTitle = computed(() => tablesItems.value.find((item) => item.value === selectedTab.value)?.title);

  const agentHeaders: Header[] = [
    {
      title: 'call_center.extensions_header.agent_name',
      key: 'agentName',
      format: (item: any) => formatNameNumber(item.agentName, item.agentNumber),
    },
    {
      title: 'call_center.extensions_header.total_calls',
      key: 'received',
      sortable: true,
    },
    {
      title: 'call_center.extensions_header.answered_calls',
      key: 'answered',
      sortable: true,
    },
    {
      title: 'call_center.extensions_header.unanswered_calls',
      key: 'abandoned',
      sortable: true,
    },
    {
      title: 'call_center.extensions_header.avg_talk_time',
      key: 'avgTalkTime',
      sortable: true,
      format: 'time',
    },
    {
      title: 'call_center.extensions_header.avg_ring_time',
      key: 'avgRingTime',
      sortable: true,
      format: 'time',
    },
    {
      title: 'call_center.extensions_header.avg_break_time',
      key: 'avgBreakTime',
      sortable: true,
      format: 'time',
    },
  ];

  const queuesHeaders: Header[] = [
    {
      title: 'call_center.queues_header.queue',
      key: 'name',
      format: (queue: any) => formatNameNumber(queue.name, queue.number),
    },
    {
      title: 'call_center.queues_header.logged',
      key: 'loggedInAgents',
      sortable: true,
    },
    {
      title: 'call_center.queues_header.available',
      key: 'availableAgents',
      sortable: true,
    },
    {
      title: 'call_center.queues_header.queued_calls',
      key: 'waitingCallsCount',
      sortable: true,
    },
    {
      title: 'call_center.queues_header.completed_calls',
      key: 'completedCalls',
      sortable: true,
    },
    {
      title: 'call_center.queues_header.abandoned_calls',
      key: 'abandonedCalls',
      sortable: true,
    },
    {
      title: 'call_center.queues_header.longest_hold_time',
      key: 'currentLongestHoldTime',
      sortable: true,
    },
    {
      title: 'call_center.queues_header.hold_time',
      key: 'averageHoldTime',
      sortable: true,
    },
    {
      title: 'call_center.queues_header.talk_time',
      key: 'averageTalkTime',
      sortable: true,
    },
  ];

  const agentsSummaryData = computed(() => {
    return {
      headers: agentHeaders,
      items: availableAgents.value,
      loading: uiFlags.value.isFetchingAvailableAgents,
    };
  });

  const queuesSummaryData = computed(() => {
    return {
      headers: queuesHeaders,
      items: Object.values(queues.value ?? {}),
      loading: uiFlags.value.isFetchingQueues,
    };
  });

  const selectedSummaryData = computed(() => {
    return selectedTab.value === 'agents' ? agentsSummaryData.value : queuesSummaryData.value;
  });

  const itemValue = computed(() => (selectedTab.value === 'agents' ? 'agentNumber' : 'number'));
</script>

<template>
  <bv-table
    :key="'table-' + selectedTab"
    :headers="selectedSummaryData.headers"
    :item-value="itemValue"
    :items="selectedSummaryData.items"
    items-per-page="5"
    :loading="selectedSummaryData.loading"
    searchable
  >
    <template #title>
      <bv-tabs
        v-model="selectedTab"
        :tabs="tablesItems"
      />
    </template>
  </bv-table>
</template>

<style scoped lang="scss">
  :deep(.v-btn__append) {
    transition: transform 0.3s ease-in-out;
  }

  .summary-menu-btn--active :deep(.v-btn__append) {
    transform: rotate(180deg);
  }
</style>
